import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as search from "@client/features/search";

import * as router from "@client/router";
import * as utils from "@client/utils";

import * as forms from "@client/site/forms";
import {AppDispatch} from "@client/store";
import {server} from "@reactivated";

export const confirmAndDeleteFood =
    (food: server.journal.nutrition.Food.output) => async (dispatch: AppDispatch) => {
        const confirmed = await dispatch(
            dialogs.confirm({
                message: "Are you sure?",
                body: "This will be deleted forever.",
            }),
        );
        if (confirmed) {
            await server.journal.nutrition.delete_food({food_id: food.id});
            void dispatch(search.loadSearch({reset: true}));
            dispatch(notify(`Your food was deleted`));
            router.push("food_list.index", {});
        }
    };

/**
 * Fakes a food to behave like an entry so we can use it in EntryList.
 */
export const getFilteredFoodsAsCalculatableEntries = (
    filter: string,
    foods: server.journal.nutrition.Food.output[],
): Array<{
    entry: server.journal.nutrition.InlineEntry.output;
    quantity: string | null;
    unit: string;
    food_id: number;
}> => {
    return foods
        .filter((food) => utils.filterPredicate(food.name, filter))
        .map((food) => ({
            ...food,
            // Not all foods have a base unit (example: corrected foods from the database that came in as "1 package"
            // So we need to fall back to this when treating this as a calculatable entry on the front end.
            quantity:
                food.base_quantity != null
                    ? food.base_quantity
                    : food.serving_size_numerator!,
            unit: food.base_unit !== "" ? food.base_unit : food.serving_size_unit,
        }))
        .map((food) => ({
            entry: {
                ...food,
                uuid: "" as UUID,
                container_quantity: null,
                container_unit: "",

                container_servings: food.container_servings ?? "1",
                serving_size_numerator: food.serving_size_numerator ?? "1",
                serving_size_denominator: "1",
                nutritional_vision_prompt: null,
                backend: "foods",
                backend_id: food.id.toString(),
                usda_food_id: null,
                expanded_recipe: [],
                additional_units: [],
                units: [
                    {
                        unit: food.base_unit,
                        multiplier: food.base_quantity ?? "1",
                    },
                    {
                        unit: food.serving_size_unit,
                        multiplier: food.serving_size_numerator ?? "1",
                    },
                ],
            },
            food_id: food.id,
            // Not all foods have a base unit (example: corrected foods from the database that came in as "1 package"
            // So we need to fall back to this when treating this as a calculatable entry on the front end.
            quantity: food.quantity,
            unit: food.unit,
        }));
};

export const useFoodForms = ({
    initial,
}: {
    initial?: Partial<
        forms.PFormHandler<server.journal.nutrition.CreateFood>["values"]
    > | null;
}) => {
    const form = forms.useAutoPform(
        server.journal.nutrition.CreateFood,
        initial ?? null,
    );

    const basicLayout = forms.usePformLayout({
        form,
        layout: [
            {
                section:
                    "Tell us a little about this item. You must tell us how much the food weighs, or its serving size information. Both is even better.",
            },
            {
                name: "name",
            },
            form.values.base_unit !== ""
                ? {
                      label: "How much does each serving weigh?",
                      fields: [{name: "base_unit"}, {name: "base_quantity"}],
                  }
                : {
                      label: "How much does each serving weigh?",
                      name: "base_unit",
                  },
            {
                name: "container_servings",
                label: "How many servings are in the container?",
            },
            {
                name: "serving_size_unit",
                label: "What do you typically call a serving? A slice? A cup?",
            },
            {
                name: "serving_size_numerator",
                label: "What is the serving quantity? Ex: For 2 slices write 2.",
            },
            {
                section: "Now tell us about its nutrition",
            },
            {
                name: "calories_pg",
                label: "Energy per serving",
            },
            {
                name: "protein_pg",
                label: "Protein per serving",
            },
            {
                name: "fat_pg",
                label: "Fat per serving",
            },
            {
                name: "carbs_pg",
                label: "Carbs per serving",
            },
            {
                name: "fiber_pg",
                label: "Fiber per serving",
            },
            {
                section: "And optionally some advanced information",
            },
            {
                name: "net_carbs_pg",
                label: "Net carbs per serving",
            },
            {
                name: "upc",
                label: "UPC code",
            },
        ],
    });

    const advancedLayout = forms.usePformLayout({
        form,
        layout: [
            {
                section: "Tell us about its macro breakdown.",
            },
            {
                name: "saturated_fat_pg",
                label: "Saturated fat per serving",
            },
            {
                name: "polyunsaturated_fat_pg",
                label: "Polyunsaturated fat per serving",
            },
            {
                name: "trans_fat_pg",
                label: "Trans fat per serving",
            },
            {
                name: "cholesterol_pg",
                label: "Cholesterol per serving",
            },
            {
                name: "sugars_pg",
                label: "Sugars per serving",
            },
            {
                section: "Tell us about its vitamins and minerals",
            },
            {
                name: "potassium_pg",
                label: "Potassium per serving",
            },
            {
                name: "sodium_pg",
                label: "Sodium per serving",
            },
            {
                name: "magnesium_pg",
                label: "Magnesium per serving",
            },
            {
                name: "vitamin_a_dv_pg",
                label: "Vitamin A DV",
            },
            {
                name: "vitamin_c_dv_pg",
                label: "Vitamin C DV",
            },
            {
                name: "calcium_dv_pg",
                label: "Calcium DV",
            },
            {
                name: "iron_dv_pg",
                label: "Iron DV",
            },
        ],
    });

    return {form, basicLayout, advancedLayout};
};
